import { z } from 'zod';

/**
 * Zod schema for the per-signage JSON config served from `/static/{signage}.json`.
 *
 * This is the single source of truth for the config format; every type in
 * `src/config/types.ts` is inferred from it. See `docs/MIGRATION.md` §2 for the field
 * table and the defaults this file encodes.
 *
 * Two rules govern every edit here (CLAUDE.md constraint 1):
 *
 * 1. The JSON files in `public/static/` are deployed on live displays. No key is ever
 *    renamed or "fixed" — including the inherited typos `layout.botomSpacing` and
 *    `slider.efect`, which are canonical here.
 * 2. Optional keys carry `.default(...)`, so a parsed config is fully resolved and no
 *    consumer writes `?? fallback`. The only exceptions are `baseTopic` and `mqttUrl`,
 *    whose defaults depend on `window`/`import.meta.env` and are therefore resolved in
 *    `loadConfig()` — that keeps this module pure and runnable under Node (see
 *    `scripts/check-configs.ts`).
 */

/**
 * `options.layout` of a single component. Widget-level and unrelated to the top-level
 * `layout`. Shared by every widget, so it lives here rather than next to one of them;
 * `useWidgetLayout()` (SYM-502) turns it into a className plus an inline style.
 *
 * `style` is a raw CSS declaration string from config (e.g. `backdrop-filter: blur(10px);`)
 * and has to be parsed into a style object before React will accept it.
 */
export const widgetLayoutSchema = z.object({
  rounded: z.string().optional(),
  backgroundColor: z.string().optional(),
  /** rem */
  padding: z.number().optional(),
  /** Raw CSS declaration string, e.g. `backdrop-filter: blur(10px);` */
  style: z.string().optional(),
});

/** Per-widget spacing of the widget containers, in rem. */
const componentSpacingSchema = z.object({
  topSpacing: z.number().default(1),
  bottomSpacing: z.number().default(1),
  sideSpacing: z.number().default(1),
});

/** Top-level `layout`: body theming plus the outer container padding. */
const signageLayoutSchema = z
  .object({
    components: componentSpacingSchema.prefault({}),
    /** rem, container padding-top */
    topSpacing: z.number().default(0),
    /**
     * [sic] rem, container padding-bottom. Misspelled in every deployed config, so this
     * is the canonical key. Left optional so the `bottomSpacing` alias below can be
     * distinguished from an explicit `0`; the transform applies the default.
     */
    botomSpacing: z.number().optional(),
    /** Correct-spelling alias. Never required, normalised into `botomSpacing`. */
    bottomSpacing: z.number().optional(),
    fontColor: z.string().default(''),
    background: z.string().default(''),
    /** `document.body.className` — deployed values are `bp` and `symbiosy-color`. */
    class: z.string().default(''),
  })
  .transform(({ bottomSpacing, ...layout }) => ({
    ...layout,
    botomSpacing: layout.botomSpacing ?? bottomSpacing ?? 0,
  }));

/** `slider.layout` — the progress bar. */
const sliderLayoutSchema = z.object({
  color: z.string().default('grey'),
  height: z.number().default(1),
});

/**
 * Slide transition. [sic] `efect` is the canonical key.
 *
 * `.catch()` rather than a plain enum: no deployed config carries a slider yet, so an
 * unknown value here is unverifiable guesswork. Rejecting the whole config over the name
 * of a CSS transition would blank a screen (CLAUDE.md constraint 4), so an unrecognised
 * value degrades to `fade`.
 */
const slideEffectSchema = z.enum(['fade', 'right-to-left']).catch('fade');

/**
 * Slider config. Deliberately **not** defaulted: `ModuleLoader` branches on the presence
 * of `slider` to choose the slider layout, so absent and `{}` must stay distinguishable.
 * Slide contents are typed by SYM-492.
 */
const sliderSchema = z
  .object({
    slides: z.array(z.unknown()).default([]),
    /** [sic] canonical key. */
    efect: slideEffectSchema.optional(),
    /** Correct-spelling alias. Never required, normalised into `efect`. */
    effect: slideEffectSchema.optional(),
    /** seconds */
    interval: z.number().default(10),
    showprogressBar: z.boolean().default(true),
    layout: sliderLayoutSchema.prefault({}),
  })
  .transform(({ effect, ...slider }) => ({
    ...slider,
    efect: slider.efect ?? effect ?? 'fade',
  }));

/**
 * One entry of `components`.
 *
 * `options` stays an opaque record on purpose. Each widget owns the schema for its own
 * options and validates them next to itself (SYM-495/496/501); modelling them centrally
 * would make this file a merge bottleneck for every widget ticket and would let one
 * widget's schema drift blank an entire screen.
 */
export const componentSchema = z.object({
  /** Resolved against the widget registry at render time. Unknown types render nothing. */
  type: z.string(),
  /** 1-based slide index, used by SYM-492. */
  slide: z.number().int().optional(),
  options: z.record(z.string(), z.unknown()).default({}),
});

export const signageConfigSchema = z.object({
  /**
   * Required marker field: it is what the legacy app used to tell a real config from
   * anything else. Note it is *not* required to equal the signage id — three deployed
   * configs disagree with their filename (`freshwaves.json` says `corio`, `szeged_3/4`
   * say `szeged_2`) and they are in production, so this is never validated against the id.
   */
  path: z.string(),
  /** Falls back to `VITE_MQTT_URL`; resolved in `loadConfig`. */
  mqttUrl: z.string().optional(),
  /** ms */
  heartBeat: z.number().int().default(10000),
  /** Falls back to `'/signage_fallback' + window.location.pathname`; resolved in `loadConfig`. */
  baseTopic: z.string().optional(),
  floorCode: z.string().default('all'),
  locale: z.string().default('en-GB'),
  scrollable: z.boolean().default(false),
  layout: signageLayoutSchema.prefault({}),
  slider: sliderSchema.optional(),
  /** Ordered. May be empty. */
  components: z.array(componentSchema),
});
